import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { passwordIssue, PASSWORD_RULE } from "@/lib/password";
import { ShieldCheckIcon, LockKeyIcon, DeviceMobileIcon, CircleNotchIcon, ArrowRightIcon, KeyIcon } from "@phosphor-icons/react";

const LOGO = "/soverenx-logo.jpg";
const inputCls = "h-11 rounded-sm bg-zinc-900 border-zinc-800 font-mono text-sm text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-1 focus-visible:ring-cyan-500";

export default function Login() {
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState("login"); // login | force_reset | enroll | verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(null);
  const [enroll, setEnroll] = useState(null); // {qr, secret}
  const [busy, setBusy] = useState(false);
  const [supportEmail, setSupportEmail] = useState("");

  useEffect(() => {
    api.get("/auth/support-contact").then(({ data }) => setSupportEmail(data.email)).catch(() => {});
  }, []);

  const proceedAfterAuthStep = (data) => {
    setPending(data.pending_token);
    if (data.stage === "enroll_2fa") { setEnroll({ qr: data.qr, secret: data.secret }); setStage("enroll"); }
    else if (data.stage === "force_reset") { setStage("force_reset"); }
    else { setStage("verify"); }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      proceedAfterAuthStep(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Login failed"));
    }
    setBusy(false);
  };

  const submitForceReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) return toast.error("Passwords do not match");
    const pwIssue = passwordIssue(newPassword);
    if (pwIssue) return toast.error(pwIssue);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/force-reset", { new_password: newPassword, confirm },
        { headers: { Authorization: `Bearer ${pending}` } });
      toast.success("Password set — now register your authenticator");
      setNewPassword(""); setConfirm("");
      proceedAfterAuthStep(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Could not set password"));
    }
    setBusy(false);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { code },
        { headers: { Authorization: `Bearer ${pending}` } });
      setAuth(data.access_token, data.user);
      toast.success("Signed in");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Verification failed"));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-4 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "radial-gradient(circle at 25% 20%, #06b6d4 0, transparent 45%)" }} />
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={LOGO} alt="SoverenX Systems" className="h-16 object-contain" data-testid="login-logo" />
        </div>

        <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 backdrop-blur-xl p-7">
          {stage === "login" && (
            <form onSubmit={submitLogin} className="space-y-5" data-testid="login-form">
              <div>
                <h1 className="font-head text-xl font-semibold text-zinc-100">Sign in</h1>
                <p className="text-sm text-zinc-500 mt-1">Access the SoverenX config engine.</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-zinc-400">Email</label>
                <Input data-testid="login-email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-zinc-400">Password</label>
                <Input data-testid="login-password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
              </div>
              <button data-testid="login-submit" disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-sm bg-cyan-500 hover:bg-cyan-400 py-2.5 text-sm font-semibold text-zinc-950 transition-colors duration-150 disabled:opacity-60">
                {busy ? <CircleNotchIcon className="h-4 w-4 animate-spin" /> : <><LockKeyIcon className="h-4 w-4" /> Continue</>}
              </button>
              <div className="pt-1 text-center">
                <span className="text-[11px] text-zinc-600">Forgot your password? </span>
                {supportEmail ? (
                  <a data-testid="contact-admin-link" href={`mailto:${supportEmail}?subject=Password%20reset%20request`}
                    className="text-[11px] text-cyan-400 hover:underline">Contact your administrator ({supportEmail})</a>
                ) : (
                  <span className="text-[11px] text-zinc-500">Contact your administrator</span>
                )}
              </div>
            </form>
          )}

          {stage === "force_reset" && (
            <form onSubmit={submitForceReset} className="space-y-5" data-testid="force-reset-form">
              <div>
                <div className="flex items-center gap-2 text-cyan-400 mb-1">
                  <KeyIcon className="h-5 w-5" />
                  <h1 className="font-head text-xl font-semibold text-zinc-100">Set a new password</h1>
                </div>
                <p className="text-sm text-zinc-500">For security, choose your own password before continuing to two-factor setup.</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-zinc-400">New password</label>
                <Input data-testid="force-reset-password" type="password" required value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 chars, 1 uppercase, 1 special" className={inputCls} />
                <p className="text-[11px] text-zinc-600">{PASSWORD_RULE}</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-zinc-400">Confirm new password</label>
                <Input data-testid="force-reset-confirm" type="password" required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className={inputCls} />
              </div>
              <button data-testid="force-reset-submit" disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-sm bg-cyan-500 hover:bg-cyan-400 py-2.5 text-sm font-semibold text-zinc-950 transition-colors duration-150 disabled:opacity-60">
                {busy ? <CircleNotchIcon className="h-4 w-4 animate-spin" /> : <>Set password <ArrowRightIcon className="h-4 w-4" /></>}
              </button>
            </form>
          )}

          {stage === "enroll" && (
            <div className="space-y-5" data-testid="enroll-2fa">
              <div>
                <div className="flex items-center gap-2 text-cyan-400 mb-1">
                  <ShieldCheckIcon className="h-5 w-5" />
                  <h1 className="font-head text-xl font-semibold text-zinc-100">Set up 2FA</h1>
                </div>
                <p className="text-sm text-zinc-500">Scan this QR code with the <span className="text-zinc-300">Microsoft or Google Authenticator</span> app, then enter the 6-digit code.</p>
              </div>
              <div className="flex justify-center">
                <div className="rounded-md bg-white p-2">
                  <img src={enroll?.qr} alt="2FA QR" className="h-44 w-44" data-testid="enroll-qr" />
                </div>
              </div>
              <div className="text-center">
                <p className="text-[11px] uppercase tracking-widest text-zinc-600">Manual key</p>
                <code className="text-xs text-cyan-300 break-all">{enroll?.secret}</code>
              </div>
              <form onSubmit={submitCode} className="space-y-3">
                <Input data-testid="enroll-code" value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="123456" inputMode="numeric" maxLength={6}
                  className={`${inputCls} text-center tracking-[0.5em] text-lg`} />
                <button data-testid="enroll-verify" disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-sm bg-cyan-500 hover:bg-cyan-400 py-2.5 text-sm font-semibold text-zinc-950 transition-colors duration-150 disabled:opacity-60">
                  {busy ? <CircleNotchIcon className="h-4 w-4 animate-spin" /> : <>Verify & enable <ArrowRightIcon className="h-4 w-4" /></>}
                </button>
              </form>
            </div>
          )}

          {stage === "verify" && (
            <form onSubmit={submitCode} className="space-y-5" data-testid="verify-2fa">
              <div>
                <div className="flex items-center gap-2 text-cyan-400 mb-1">
                  <DeviceMobileIcon className="h-5 w-5" />
                  <h1 className="font-head text-xl font-semibold text-zinc-100">Two-factor code</h1>
                </div>
                <p className="text-sm text-zinc-500">Enter the 6-digit code from your authenticator app.</p>
              </div>
              <Input data-testid="verify-code" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="123456" inputMode="numeric" maxLength={6} autoFocus
                className={`${inputCls} text-center tracking-[0.5em] text-lg`} />
              <button data-testid="verify-submit" disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-sm bg-cyan-500 hover:bg-cyan-400 py-2.5 text-sm font-semibold text-zinc-950 transition-colors duration-150 disabled:opacity-60">
                {busy ? <CircleNotchIcon className="h-4 w-4 animate-spin" /> : <>Verify <ArrowRightIcon className="h-4 w-4" /></>}
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-[11px] text-zinc-600 mt-6 tracking-wide">SOVERENX SYSTEMS · SECURE CONFIG ENGINE</p>
      </div>
    </div>
  );
}
